import * as React from 'react';
import ResizeObserver from 'resize-observer-polyfill';

import { hasHorizontalScrollOverflow } from '../utils/horizontalScrollEdge';
import {
  resolveScrollElement,
  ScrollContainerTarget
} from '../utils/scrollContainerTarget';

export type UseHasHorizontalScrollOptions = {
  enabled?: boolean;
  /** список зависимостей, отслеживаемых для изменения hasHorizontalScroll */
  deps?: readonly unknown[];
};

const useHasHorizontalScroll = (
  target: ScrollContainerTarget,
  options: UseHasHorizontalScrollOptions = {}
): boolean => {
  const { enabled = true, deps = [] } = options;
  const [hasHorizontalScroll, setHasHorizontalScroll] = React.useState(false);

  const measure = React.useCallback(() => {
    const element = resolveScrollElement(target);
    if (!element) {
      return;
    }
    const next = hasHorizontalScrollOverflow(
      element.scrollWidth,
      element.clientWidth
    );
    setHasHorizontalScroll((prev) => (prev === next ? prev : next));
  }, [target]);

  React.useEffect(() => {
    if (!enabled) {
      setHasHorizontalScroll(false);
      return;
    }

    const container = resolveScrollElement(target);
    if (!container) {
      return;
    }

    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(container);
    window.addEventListener('resize', measure);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [enabled, target, measure, ...deps]);

  return hasHorizontalScroll;
};

export default useHasHorizontalScroll;
